/**
 * TIP CHOICES, SURCHARGE RATE & CARD MINIMUM — editable in Super Admin, with
 * the merchant's controls (platform-app TerminalPaymentTypes). Purely
 * controlled: edits a draft; the page's one save bar writes it. Model and
 * rules: ./locationValues.ts.
 */
import React, { useState } from 'react';
import { DollarSign, Percent, MinusSquare, Plus, X } from 'lucide-react';
import { Card, Row, Pill, ScopeBadge, TextAction } from './ui';
import { activeAgreementOf } from './model';
import {
  type LocationValues, type LocationDraft, MAX_TIP_CHOICES, parseDec, parseOverride, tipWord,
} from './locationValues';

const chipOn = 'border-ruby bg-[#FFF1F3] text-ruby-deep';
const chipOff = 'border-stone-200 bg-white text-stone-500';

const MoneyInput: React.FC<{ label: string; value: string; onChange: (v: string) => void; onBlur?: () => void; prefix?: string; suffix?: string; placeholder?: string; disabled?: boolean }> = ({ label, value, onChange, onBlur, prefix, suffix, placeholder, disabled }) => (
  <span className="inline-flex h-11 items-center rounded-lg border border-stone-300 bg-white px-2 focus-within:border-ruby focus-within:ring-2 focus-within:ring-rose-200">
    {prefix && <span className="text-sm text-stone-500">{prefix}</span>}
    <input aria-label={label} inputMode="decimal" value={value} placeholder={placeholder} disabled={disabled}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9.$]/g, ''))} onBlur={onBlur}
      className="w-20 border-0 bg-transparent text-right text-sm tabular-nums text-stone-900 outline-none focus-visible:outline-none placeholder:text-stone-400" />
    {suffix && <span className="text-sm text-stone-500">{suffix}</span>}
  </span>
);

export const LocationValuesEditor: React.FC<{
  gateway: any | null; locationName: string; approved: boolean;
  base: LocationValues; draft: LocationDraft; onChange: (d: LocationDraft) => void; disabled?: boolean;
}> = ({ gateway, locationName, approved, base, draft, onChange, disabled }) => {
  const [newPct, setNewPct] = useState('');
  const agreement = activeAgreementOf(gateway);
  const maxRate = Number(gateway?.maxSurchargeRate ?? agreement?.maxRate ?? 0);
  const maxFlat = Number(gateway?.maxSurchargeFlat ?? agreement?.maxFlat ?? 0);
  const suggestion = !base.tip && !draft.tipAccepted;
  const tip = draft.tip;
  const setTip = (t: typeof tip) => onChange({ ...draft, tip: t, tipAccepted: true });
  const addPct = () => {
    const n = parseDec(newPct);
    if (n === null || n <= 0 || n > 100 || tip.percentages.includes(n) || tip.percentages.length >= MAX_TIP_CHOICES) return;
    setTip({ ...tip, percentages: [...tip.percentages, n].sort((a, b) => a - b) });
    setNewPct('');
  };
  const clampOverride = (key: 'rateStr' | 'flatStr', max: number) => {
    const v = parseOverride(draft[key]);
    if (v === null) { onChange({ ...draft, [key]: '' }); return; }
    let n = v === undefined ? 0 : v;
    if (max > 0 && n > max) n = max;
    onChange({ ...draft, [key]: n.toFixed(2) });
  };
  const gwRead = !!gateway;

  return (
    <Card
      title="Tip choices, surcharge rate & card minimum"
      sub={<>Changing these changes <b>every till at {locationName}</b>.</>}
      badge={<ScopeBadge kind="loc">Every till at location</ScopeBadge>}
    >
      <Row testId="pt-loc-tips" icon={<DollarSign size={18} />}
        title={<>Tip choices {suggestion ? <Pill tone="warn">Default — not saved</Pill> : null}</>}
        desc={gwRead ? 'One list, used by Cash, Credit and Debit wherever tips are on' : 'Could not read the location settings.'}
        right={suggestion && gwRead ? (
          <TextAction testId="pt-loc-keep-defaults" disabled={disabled} onClick={() => onChange({ ...draft, tipAccepted: true })}>Use these</TextAction>
        ) : undefined}>
        {gwRead && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {tip.percentages.map((p) => (
              <span key={p} className={`inline-flex h-11 items-center gap-1 rounded-full border pl-3 pr-1 text-[13px] ${chipOn}`}>
                {p}% <span className="text-[11px] text-stone-500">· {tipWord(tip.percentages, p)}</span>
                <button type="button" aria-label={`Remove ${p}%`} disabled={disabled}
                  onClick={() => setTip({ ...tip, percentages: tip.percentages.filter((x) => x !== p) })}
                  className="grid h-9 w-9 place-items-center rounded-full text-stone-400 hover:text-stone-700">
                  <X size={14} />
                </button>
              </span>
            ))}
            <span className="inline-flex h-11 items-center rounded-full border border-stone-200 bg-white pl-3 pr-1">
              <input inputMode="decimal" aria-label="New tip percentage" placeholder="%" value={newPct} disabled={disabled}
                onChange={(e) => setNewPct(e.target.value.replace(/[^0-9.]/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPct(); } }}
                className="w-10 border-0 bg-transparent text-center text-sm text-stone-800 outline-none focus-visible:outline-none" />
              <button type="button" onClick={addPct} disabled={disabled} className="inline-flex h-9 items-center gap-1 px-2 text-sm text-stone-600 hover:text-stone-900">
                <Plus size={14} /> Add
              </button>
            </span>
            <button type="button" aria-pressed={tip.allowCustom} disabled={disabled} onClick={() => setTip({ ...tip, allowCustom: !tip.allowCustom })}
              className={`h-11 rounded-full border px-3.5 text-[13px] ${tip.allowCustom ? chipOn : chipOff}`}>Custom amount</button>
            <button type="button" aria-pressed={tip.allowSkip} disabled={disabled} onClick={() => setTip({ ...tip, allowSkip: !tip.allowSkip })}
              className={`h-11 rounded-full border px-3.5 text-[13px] ${tip.allowSkip ? chipOn : chipOff}`}>No tip</button>
          </div>
        )}
      </Row>

      {approved && (
        <Row testId="pt-loc-rate" icon={<Percent size={18} />} title="Surcharge rate"
          desc={<>Max allowed: {maxRate.toFixed(2)}% + ${maxFlat.toFixed(2)}{agreement ? ` · ${agreement.agreementName || agreement.fileName || 'active agreement'}` : ''}. Empty = the maximum.</>}
          right={(
            <span className="flex items-center gap-1.5">
              <MoneyInput label="Surcharge rate percent" value={draft.rateStr} suffix="%" placeholder={`max ${maxRate.toFixed(2)}`} disabled={disabled}
                onChange={(v) => onChange({ ...draft, rateStr: v })} onBlur={() => clampOverride('rateStr', maxRate)} />
              <span className="text-stone-400">+</span>
              <MoneyInput label="Surcharge flat fee" value={draft.flatStr} prefix="$" placeholder={`max ${maxFlat.toFixed(2)}`} disabled={disabled}
                onChange={(v) => onChange({ ...draft, flatStr: v })} onBlur={() => clampOverride('flatStr', maxFlat)} />
            </span>
          )}
        />
      )}

      <Row testId="pt-loc-min" icon={<MinusSquare size={18} />} title="Minimum card amount"
        desc="Under this, the Card tile is disabled and says why. Cash is unaffected. $0 = no minimum."
        right={(
          <MoneyInput label="Minimum card amount" value={draft.minStr} prefix="$" disabled={disabled}
            onChange={(v) => onChange({ ...draft, minStr: v })}
            onBlur={() => { const n = parseDec(draft.minStr); onChange({ ...draft, minStr: (n ?? 0).toFixed(2) }); }} />
        )}
      />
    </Card>
  );
};

export default LocationValuesEditor;
